import Tooltip from '@material-ui/core/Tooltip'
import InfoIcon from '@material-ui/icons/Info'
import { ReactNode } from 'react'
import { FlexBox, Grid } from 'shared/components/Base/Base'
import { Box } from 'shared/components/Box/Box'
import { Text } from 'shared/components/Text/Text'
import { useRepaymentsBreakdowns } from 'shared/hooks/query/useRepaymentsBreakdowns'
import { RepaymentRiskTier, RiskTierText, RiskTierTooltipText } from 'shared/models/repayments'
import { TeamStatus, TeamV3 } from 'shared/models/team'
import { TeamEntitlements } from 'shared/pages/ProfilePage/Profile/TeamEntitlements'
import {
  darkGray,
  error,
  errorD1,
  information,
  marigoldOrange,
  primaryBlack,
  royalBlue,
  warning,
} from 'shared/theme/colors'
import styled from 'styled-components'

import { consoleTooltipStyles } from '../Common/Tooltip'
import { TeamMembers } from './TeamMembers'

interface TeamHeaderProps {
  team: TeamV3
  isInsideSidebar?: boolean
  renderOpenNotesCard: () => ReactNode
}

const TEAM_STATUS_PROPS: Record<
  TeamStatus,
  {
    description: string
    color: string
    backgroundColor: string
  }
> = {
  [TeamStatus.APPROVED]: {
    description: 'Approved',
    color: royalBlue,
    backgroundColor: information,
  },
  [TeamStatus.APPLYING]: {
    description: 'Applying',
    color: marigoldOrange,
    backgroundColor: warning,
  },
  [TeamStatus.OFFBOARDED]: {
    description: 'Offboarded',
    color: errorD1,
    backgroundColor: error,
  },
  [TeamStatus.OFFBOARDING]: {
    description: 'Offboarding in progress',
    color: marigoldOrange,
    backgroundColor: warning,
  },
  [TeamStatus.DEACTIVATED]: {
    description: 'Deactivated',
    color: errorD1,
    backgroundColor: error,
  },
  [TeamStatus.ONBOARDING]: {
    description: 'Onboarding in progress',
    color: marigoldOrange,
    backgroundColor: warning,
  },
}

export function TeamHeader({ team, isInsideSidebar, renderOpenNotesCard }: TeamHeaderProps) {
  const tooltipClasses = consoleTooltipStyles()
  const { repaymentsBreakdowns } = useRepaymentsBreakdowns({ teamId: team?.id })

  // Use team status if available, otherwise fallback to isApproved
  const teamStatus = team.status ?? (team.isApproved ? [TeamStatus.APPROVED] : [TeamStatus.APPLYING])
  const teamStatusProps = !!teamStatus && TEAM_STATUS_PROPS[teamStatus]

  if (!team) {
    return null
  }

  return (
    <Grid columns={`minmax(180px, 1fr) 1fr`} smColumns="1fr" gap="24px" alignItems="flex-start">
      <FlexBox isColumn alignItems="flex-start">
        <FlexBox alignItems="center" gap="16px" mb={2}>
          <TeamEntitlements team={team} isInsideSidebar={isInsideSidebar} />
        </FlexBox>

        <Text variant="header2" fontColor={primaryBlack} mb={2}>
          {team.name}
        </Text>

        <TeamMembers teamId={team.id} titleRender={(total) => `Fleet: ${total} total`} />

        <Box mt={1.5} />

        <FlexBox gap="8px" alignItems="center" mb={2}>
          <Text variant="header3" fontColor={darkGray}>
            Fleet liability: {RiskTierText[repaymentsBreakdowns?.risk_tier || RepaymentRiskTier.LOW]}
          </Text>
          <Tooltip
            title={
              <Text variant="paragraph2" fontColor={darkGray}>
                {RiskTierTooltipText[repaymentsBreakdowns?.risk_tier || RepaymentRiskTier.LOW]}
              </Text>
            }
            placement="right"
            classes={{ tooltip: tooltipClasses.tooltip }}
          >
            <InfoIcon className={tooltipClasses.infoIcon} color="primary" fontSize="small" />
          </Tooltip>
        </FlexBox>

        {teamStatusProps && (
          <ApprovalPill
            variant="header3"
            fontColor={teamStatusProps.color}
            $backgroundColor={teamStatusProps.backgroundColor}
          >
            {teamStatusProps.description}
          </ApprovalPill>
        )}
      </FlexBox>

      <FlexBox mb={2}>{renderOpenNotesCard()}</FlexBox>
    </Grid>
  )
}

const ApprovalPill = styled(Text)<{ $backgroundColor: string }>`
  background: ${(props) => props.$backgroundColor};
  color: ${(props) => props.color};
  display: flex;
  height: 32px;
  padding: 3px 12px 4px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 3px;
`
